import { authIdentity } from "@defuse-protocol/internal-utils"
import type { AuthMethod } from "@defuse-protocol/internal-utils"
import type { GetBalancesResponse } from "@defuse-protocol/one-click-sdk-typescript"
import { nearClient } from "@src/components/DefuseSDK/constants/nearClient"
import queryClient from "@src/constants/queryClient"
import { logger } from "@src/utils/logger"
import { type QueryClient, QueryObserver } from "@tanstack/query-core"
import {
  type ActorRef,
  type Snapshot,
  type SnapshotFrom,
  assertEvent,
  assign,
  enqueueActions,
  fromCallback,
  sendTo,
  setup,
} from "xstate"
import { invalidateBalanceQueries } from "../../queries/balanceQueries"
import { getDepositedBalances } from "../../services/defuseBalanceService"
import { getTransitBalance } from "../../services/getTransitBalance"
import type { BaseTokenInfo, TokenInfo, TokenValue } from "../../types/base"
import type { IntentsUserId } from "../../types/intentsUserId"
import {
  computeTotalBalanceDifferentDecimals,
  getUnderlyingBaseTokenInfos,
} from "../../utils/tokenUtils"
import { privateBalanceQueryKey } from "../account/queries/privateBalanceQueryKey"
import { buildPrivateBalanceMap } from "../account/utils/privateHoldingsMapper"

export interface Input {
  parentRef?: ParentActor
  tokenList: TokenInfo[]
  isConfidential?: boolean
}

export type BalanceMapping = Record<BaseTokenInfo["defuseAssetId"], bigint>

type ParentReceivedEvents = {
  type: "BALANCE_CHANGED"
  params: {
    changedBalanceMapping: BalanceMapping
    changedTransitBalanceMapping: BalanceMapping
  }
}
type ParentActor = ActorRef<Snapshot<unknown>, ParentReceivedEvents>

type SharedEvents = {
  type: "UPDATE_BALANCE_SLICE"
  params: {
    balanceSlice: BalanceMapping
    transitBalanceSlice: BalanceMapping
  }
}
type ThisActor = ActorRef<Snapshot<unknown>, SharedEvents>

export type Events =
  | { type: "LOGOUT" | "REQUEST_BALANCE_REFRESH" }
  | {
      type: "LOGIN"
      params: { userAddress: string; userChainType: AuthMethod }
    }
  | {
      type: "ADD_ACCOUNT"
      params: { accountId: IntentsUserId }
    }
  | {
      type: "REMOVE_ACCOUNT"
      params: { accountId: IntentsUserId }
    }

const balancePollerActor = fromCallback<
  | {
      type: "ADD_ACCOUNT" | "REMOVE_ACCOUNT"
      params: { accountId: IntentsUserId }
    }
  | {
      type: "REFETCH"
    },
  {
    tokenIds: string[]
    accountId: IntentsUserId
    parentRef: ThisActor
    // Immutable after init — toggling private mode relies on SwapUIMachineProvider's `key` prop to remount.
    isConfidential: boolean
  }
>(({ input, receive }) => {
  const accounts = new Set([input.accountId])
  let seededFromCache = false

  receive((event) => {
    switch (event.type) {
      case "ADD_ACCOUNT":
        logger.trace("Add account to balance polling actor", {
          accountId: event.params.accountId,
        })
        accounts.add(event.params.accountId)
        break
      case "REMOVE_ACCOUNT":
        logger.trace("Remove account from balance polling actor", {
          accountId: event.params.accountId,
        })
        accounts.delete(event.params.accountId)
        break
      case "REFETCH":
        restartInterval()
        break
    }
  })

  function emitBalanceSlice(mergedBalance: BalanceMapping) {
    queryClient.setQueryData(
      [
        "intents_sdk.deposited_balance",
        { userId: input.accountId, tokenIds: input.tokenIds },
      ],
      mergedBalance
    )

    input.parentRef.send({
      type: "UPDATE_BALANCE_SLICE",
      params: {
        balanceSlice: mergedBalance,
        transitBalanceSlice: {},
      },
    })
  }

  function privateBalanceToMapping(
    privateBalance: GetBalancesResponse
  ): BalanceMapping {
    const mergedBalance = buildPrivateBalanceMap(privateBalance)
    // Private balance API omits zero-balance tokens; explicitly set missing
    // tokens to 0n to clear stale balances after a full swap.
    for (const tokenId of input.tokenIds) {
      if (!(tokenId in mergedBalance)) {
        mergedBalance[tokenId] = 0n
      }
    }
    return mergedBalance
  }

  async function fetchBalanceAndEmit() {
    try {
      if (input.isConfidential) {
        const queryKey = privateBalanceQueryKey(input.accountId)

        // Seed once from shared RQ cache; later ticks emit network results only.
        if (!seededFromCache) {
          seededFromCache = true
          const cached =
            queryClient.getQueryData<GetBalancesResponse>(queryKey)
          if (cached != null) {
            emitBalanceSlice(privateBalanceToMapping(cached))
          }
        }

        // Lazy import: privateIntents.ts runs z.string().parse(ONE_CLICK_URL) at module load, crashing tests without the env var.
        const { getPrivateBalance } = await import("./privateIntents")
        const result = await getPrivateBalance()
        if ("err" in result) {
          logger.error(`Private balance fetch failed: ${result.err}`)
          return
        }

        emitBalanceSlice(privateBalanceToMapping(result.ok))
        return
      }

      const status = await nearClient.status()
      const blockHeight = status.sync_info.latest_block_height

      const balances = await Promise.all(
        Array.from(accounts).map((accountId) => {
          return getDepositedBalances(
            accountId,
            input.tokenIds,
            nearClient,
            blockHeight
          )
        })
      )

      emitBalanceSlice(mergeBalance(balances))
    } catch (err: unknown) {
      logger.error(err)
    }
  }

  function dispose() {
    if (timer != null) {
      clearInterval(timer)
    }
  }

  let timer: ReturnType<typeof setInterval> | null = null
  function restartInterval() {
    dispose()
    void fetchBalanceAndEmit()
    timer = setInterval(() => {
      void fetchBalanceAndEmit()
    }, 10000)
  }

  restartInterval()

  return () => {
    dispose()
  }
})

export const depositedBalanceMachine = setup({
  types: {
    context: {} as {
      parentRef: ParentActor | undefined
      accountId: IntentsUserId | null
      tokenIds: string[]
      balances: BalanceMapping
      transitBalances: BalanceMapping
      transitBalanceQueryObserver: TransitBalanceQueryObserver
      isConfidential: boolean
      balancesLoaded: boolean
    },
    events: {} as Events | SharedEvents,
    input: {} as Input,
  },
  actors: {
    balancePollerActor: balancePollerActor,
    getTransitBalances: fromCallback(
      ({
        input,
      }: {
        input: {
          observer: TransitBalanceQueryObserver
          parentRef: ThisActor
          isConfidential: boolean
        }
      }) => {
        if (input.isConfidential) return

        return input.observer.subscribe((result) => {
          if (result.isSuccess) {
            input.parentRef.send({
              type: "UPDATE_BALANCE_SLICE",
              params: {
                balanceSlice: {},
                transitBalanceSlice: result.data,
              },
            })
          }
        })
      }
    ),
  },
  actions: {
    updateTransitBalanceQueryObserver: (
      { context },
      user: IntentsUserId | null
    ) => {
      const queryKey = structuredClone(
        context.transitBalanceQueryObserver.options.queryKey
      )
      queryKey[1].userId = user

      context.transitBalanceQueryObserver.setOptions({
        ...context.transitBalanceQueryObserver.options,
        queryKey: queryKey,
        queryHash: undefined,
      })
    },
    updateBalance: enqueueActions(
      (
        { enqueue, context },
        params: {
          balanceSlice: BalanceMapping
          transitBalanceSlice: BalanceMapping
        }
      ) => {
        const balanceChanged: BalanceMapping = {}
        const transitBalanceChanged: BalanceMapping = {}

        for (const [key, val] of Object.entries(params.balanceSlice)) {
          if (context.balances[key] !== val) {
            balanceChanged[key] = val
          }
        }

        for (const [key, val] of Object.entries(params.transitBalanceSlice)) {
          if (context.transitBalances[key] !== val) {
            transitBalanceChanged[key] = val
          }
        }

        const becameLoaded = !context.balancesLoaded
        const hasBalanceChanges = Object.keys(balanceChanged).length > 0
        const hasTransitBalanceChanges =
          Object.keys(transitBalanceChanged).length > 0

        if (becameLoaded) {
          enqueue.assign({ balancesLoaded: true })
        }

        // First load should notify the parent even if all balance values stayed unchanged.
        if (becameLoaded || hasBalanceChanges || hasTransitBalanceChanges) {
          if (hasBalanceChanges || hasTransitBalanceChanges) {
            enqueue.assign({
              balances: () => ({ ...context.balances, ...balanceChanged }),
              transitBalances: transitBalanceChanged,
            })
          }
          enqueue(({ context }) => {
            context.parentRef?.send({
              type: "BALANCE_CHANGED",
              params: {
                changedBalanceMapping: balanceChanged,
                changedTransitBalanceMapping: transitBalanceChanged,
              },
            })
          })
        }
      }
    ),
    clearBalance: assign({
      balances: {},
      transitBalances: {},
      balancesLoaded: false,
    }),
    refetch: sendTo("balancePollerRef", {
      type: "REFETCH",
    }),
    addAccount: sendTo(
      "balancePollerRef",
      (_, params: { accountId: IntentsUserId }) => {
        return {
          type: "ADD_ACCOUNT",
          params,
        }
      }
    ),
    removeAccount: sendTo(
      "balancePollerRef",
      (_, params: { accountId: IntentsUserId }) => {
        return {
          type: "REMOVE_ACCOUNT",
          params,
        }
      }
    ),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QTABwPawJYBdICEBDAG0IDsBjMAYgBkB5AcQEkA5AbQAYBdRUDbDizoyfEAA9EAWgCMAZk4A6AOwBOAKwyALJ1UA2ZXLnqANCACe0mQA45ivda2G9WgExyt15VoC+PsygCuAQk5FSKhACuOAAWYGRCFIR4EHRM9ACqACpcvEggQUIiYpIIssaKqsq6Mhqunpw2ymaWZR4qWnWOnHKunH3WfgFomMEQRKSUYBHRcQlYSSnUAEoAogCKGasAylkA+vgAgrSHrADCq3trAGJr2wASuWKFwqL5pTKainK2ejI2tXUyiBrhaVj0dh+7lc6nqnGsek4vn8IECoxSEzC0yisXiiWSkEUACcwAAzEmwGJYMhQAAEACNQlNqBARNNqQA3dAAa2mpLAOAoMUxU2WZKe+RexXeiH+qkU6ms1iRCmqcj0MK0YIQf0UjS06us6gN3j0qkGKLRghCk3COLm+JSxLJFKpNIZTKo1AyAAUACKHLKXI4nc6Xba0ZgXCX8dGvEqIWEyFSfGR-VxqWFG7VSAyKBEyJHKQvGLTqPRDVEja3jT3Y2Z4hYEiCKLAQYg0cSwHAEiKkvBEgAU6k4o4AlCzq2MRXaG-NFoS2x2YwU49LQKUPMmtNoNRDNY5QRZELr9YbjXJTea-CiyOgUPB8lbp3Xnmu3hvpD8lOpVB4EUq1R6C4OZGNY3zWO4qZKoWqiuBWlpThidaKJEZD2o2C4QG+gjxjKbS9Aqf6eA4XicMBWrHggSgaD0Zb6B4nDeEaFrDIUNpYjMuLzs2OG4Hhn46nYGh6OomguMoaiuNoOYwkoSrQv02ieJ4riVs+yG2vW3GOoSJLknAbp0oyWl8UUH4SLK-yVOWYlpk4UkyVRubgUqciAvBMIaloyJseiHFTFxDpNk6S5gGZAmWQgrjWMmnC-jFiJKgaRiUa08h2BCqiqJ0kHGuoqo3j4QA */
  id: "depositedBalance",

  initial: "unauthenticated",

  context: ({ input }) => {
    const tokenIds = input.tokenList
      .flatMap((t) => getUnderlyingBaseTokenInfos(t))
      .map((t) => t.defuseAssetId)

    return {
      tokenIds,
      transitBalanceQueryObserver:
        createTransitBalanceQueryObserver(queryClient),
      balances: {},
      transitBalances: {},
      parentRef: input.parentRef,
      accountId: null,
      isConfidential: input.isConfidential ?? false,
      balancesLoaded: false,
    }
  },

  states: {
    unauthenticated: {},

    authenticated: {
      invoke: [
        {
          id: "balancePollerRef",
          src: "balancePollerActor",
          input: ({ self, context, event }) => {
            assertEvent(event, "LOGIN")

            return {
              accountId: authIdentity.authHandleToIntentsUserId(
                event.params.userAddress,
                event.params.userChainType
              ),
              tokenIds: context.tokenIds,
              parentRef: self,
              isConfidential: context.isConfidential,
            }
          },
        },
        {
          src: "getTransitBalances",
          input: ({ self, context }) => ({
            parentRef: self,
            observer: context.transitBalanceQueryObserver,
            isConfidential: context.isConfidential,
          }),
        },
      ],

      on: {
        LOGOUT: {
          target: "unauthenticated",
          actions: [
            assign({ accountId: null }),
            "clearBalance",
            { type: "updateTransitBalanceQueryObserver", params: null },
            () => {
              queryClient.removeQueries({
                queryKey: ["intents_sdk.deposited_balance"],
              })
            },
          ],
        },

        REQUEST_BALANCE_REFRESH: {
          actions: ["refetch", invalidateBalanceQueries],
        },

        UPDATE_BALANCE_SLICE: {
          actions: {
            type: "updateBalance",
            params: ({ event }) => ({
              balanceSlice: event.params.balanceSlice,
              transitBalanceSlice: event.params.transitBalanceSlice,
            }),
          },
        },

        ADD_ACCOUNT: {
          actions: [
            {
              type: "addAccount",
              params: ({ event }) => event.params,
            },
            "refetch",
          ],
        },

        REMOVE_ACCOUNT: {
          actions: {
            type: "removeAccount",
            params: ({ event }) => event.params,
          },
        },
      },
    },
  },

  on: {
    LOGIN: {
      guard: ({ event, context }) => {
        const accountId = authIdentity.authHandleToIntentsUserId(
          event.params.userAddress,
          event.params.userChainType
        )
        return context.accountId !== accountId
      },
      target: ".authenticated",
      actions: [
        assign({
          accountId: ({ event }) =>
            authIdentity.authHandleToIntentsUserId(
              event.params.userAddress,
              event.params.userChainType
            ),
        }),
        "clearBalance",
        {
          type: "updateTransitBalanceQueryObserver",
          params: ({ event }) =>
            authIdentity.authHandleToIntentsUserId(
              event.params.userAddress,
              event.params.userChainType
            ),
        },
      ],
      reenter: true,
    },
  },
})

type BalancesSelectorReturn<T> = T extends undefined
  ? undefined
  : BalanceMapping

export function balancesSelector<
  T extends SnapshotFrom<typeof depositedBalanceMachine> | undefined,
>(state: T): BalancesSelectorReturn<T> {
  if (state === undefined) {
    return undefined as BalancesSelectorReturn<T>
  }
  return state.context.balances as BalancesSelectorReturn<T>
}

export function isBalancesLoadedSelector(
  state: undefined | SnapshotFrom<typeof depositedBalanceMachine>
): boolean {
  return state?.context.balancesLoaded ?? false
}

export function balanceSelector(token: TokenInfo | null | undefined) {
  return (state: undefined | SnapshotFrom<typeof depositedBalanceMachine>) => {
    if (!state || !token) return
    return computeTotalBalanceDifferentDecimals(token, state.context.balances)
  }
}

export function balanceAllSelector<
  const T extends
    | Record<PropertyKey, TokenInfo | null>
    | Array<TokenInfo | null>,
>(arg: T) {
  return <S extends undefined | SnapshotFrom<typeof depositedBalanceMachine>>(
    state: S
  ): S extends undefined
    ? undefined
    : S extends SnapshotFrom<typeof depositedBalanceMachine>
      ? { [K in keyof T]: TokenValue | undefined }
      : undefined => {
    // @ts-expect-error Need TS wizard to help with this
    if (!state) return

    if (Array.isArray(arg)) {
      const result = arg.map((token) => {
        if (token == null) return

        return computeTotalBalanceDifferentDecimals(
          token,
          state.context.balances
        )
      })
      // @ts-expect-error Need TS wizard to help with this
      return result
    }

    const result = Object.fromEntries(
      Object.entries(arg).map(([key, token]) => {
        if (token == null) return [key, undefined]

        return [
          key,
          computeTotalBalanceDifferentDecimals(token, state.context.balances),
        ]
      })
    )
    // @ts-expect-error Need TS wizard to help with this
    return result
  }
}

export function transitBalanceSelector(token: TokenInfo | null | undefined) {
  return (state: undefined | SnapshotFrom<typeof depositedBalanceMachine>) => {
    if (!state || !token) return

    const pending = computeTotalBalanceDifferentDecimals(
      token,
      state.context.transitBalances,
      {
        strict: false,
      }
    )

    if (pending?.amount === 0n) return
    return pending
  }
}

type TransitBalanceQueryObserver = ReturnType<
  typeof createTransitBalanceQueryObserver
>

function createTransitBalanceQueryObserver(queryClient: QueryClient) {
  return new QueryObserver(queryClient, {
    queryKey: ["intents_sdk.transit_balance", { userId: null }] as [
      string,
      { userId: null | IntentsUserId },
    ],
    queryFn: ({ queryKey }) => {
      if (queryKey[1].userId == null) {
        throw new Error("userId is null")
      }

      return getTransitBalance(queryKey[1].userId)
    },
    enabled: (query) => query.queryKey[1].userId != null,
    refetchInterval: 10000,
  })
}

function mergeBalance(balances: BalanceMapping[]): BalanceMapping {
  if (balances.length === 1) {
    return balances[0]
  }

  const mergedBalance: BalanceMapping = {}
  for (const balance of balances) {
    for (const [k, v] of Object.entries(balance)) {
      mergedBalance[k] = (mergedBalance[k] ?? 0n) + v
    }
  }
  return mergedBalance
}
